// hooks/usePromotions.js
import { useState, useEffect, useCallback } from 'react';
import promotionService from '../services/PromotionService';
import promotionPerformanceMonitor from '../utils/PromotionPerformanceMonitor';

const usePromotions = (initialPageSize = 30) => {
  const [promotions, setPromotions] = useState([]);
  const [allPromotions, setAllPromotions] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(initialPageSize);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Fetch initial promotions with optimization and progress tracking
  const fetchPromotions = useCallback(async (forceRefresh = false) => {
    try {
      if (!forceRefresh) {
        setLoading(true);
        setLoadingProgress(0);
        promotionPerformanceMonitor.startInitialLoad();
      } else {
        setRefreshing(true);
        setLoadingProgress(0);
      }
      
      setError(null);
      
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev < 90) {
            return prev + Math.random() * 15;
          }
          return prev;
        });
      }, 200);
      
      // Fetch only initial batch for fast loading
      const result = await promotionService.fetchInitialPromotions(pageSize, forceRefresh);
      
      clearInterval(progressInterval);
      setLoadingProgress(100);
      
      if (result.success) {
        setPromotions(result.data.promotions);
        setCategories(result.data.categories);
        setLastUpdated(new Date());
        setHasMore(result.data.hasMore);
        
        // Reset pagination
        setCurrentPage(1);
        
        // Set all promotions from cache if available
        const cachedData = promotionService.getCachedPromotions();
        if (cachedData) {
          setAllPromotions(cachedData.promotions);
        } else {
          setAllPromotions(result.data.promotions);
        }

        // Track performance
        if (result.fromCache) {
          promotionPerformanceMonitor.trackCacheHit();
        } else {
          promotionPerformanceMonitor.trackCacheMiss();
        }
        
        if (!forceRefresh) {
          promotionPerformanceMonitor.endInitialLoad(result.data.promotions.length);
        }
      } else {
        setError(result.error);
        setPromotions([]);
        setCategories(['All']);
        setAllPromotions([]);
      }
    } catch (err) {
      console.error('Error fetching promotions:', err);
      setError('Failed to load promotions');
      setPromotions([]);
      setCategories(['All']);
      setAllPromotions([]);
    } finally {
      // Small delay to show 100% before hiding
      setTimeout(() => {
        setLoading(false);
        setRefreshing(false);
        setLoadingProgress(0);
      }, 500);
    }
  }, [pageSize]);

  // Load more promotions (pagination)
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    
    // Check if we have more data in cache
    const cachedData = promotionService.getCachedPromotions();
    if (cachedData) {
      setAllPromotions(cachedData.promotions);
    }
    
    // Simulate async loading for smooth UX
    setTimeout(() => {
      const nextPage = currentPage + 1;
      const startIndex = (nextPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      
      const sourcePromotions = cachedData ? cachedData.promotions : allPromotions;
      const newPromotions = sourcePromotions.slice(startIndex, endIndex);
      
      if (newPromotions.length > 0) {
        setPromotions(prev => [...prev, ...newPromotions]);
        setCurrentPage(nextPage);
        setHasMore(endIndex < sourcePromotions.length);
      } else {
        setHasMore(false);
      }
      
      setLoadingMore(false);
    }, 200); // Reduced delay for faster loading
  }, [currentPage, pageSize, allPromotions, loadingMore, hasMore]);

  // Refresh promotions
  const refreshPromotions = useCallback(() => {
    fetchPromotions(true);
  }, [fetchPromotions]);

  // Reset to first page with filtered data
  const resetToFirstPage = useCallback((filteredPromotions = null) => {
    setCurrentPage(1);
    const sourcePromotions = filteredPromotions || allPromotions;
    if (sourcePromotions && sourcePromotions.length >= 0) {
      const firstPagePromotions = sourcePromotions.slice(0, pageSize);
      setPromotions(firstPagePromotions);
      setHasMore(sourcePromotions.length > pageSize);
    }
  }, [pageSize, allPromotions]);

  // Initial fetch
  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  return {
    // Data
    promotions,
    allPromotions,
    categories,
    lastUpdated,
    
    // Loading states
    loading,
    refreshing,
    loadingMore,
    loadingProgress,
    error,
    
    // Pagination
    currentPage,
    pageSize,
    hasMore,
    
    // Actions
    refreshPromotions,
    loadMore,
    resetToFirstPage,
    
    // Computed values
    totalPromotions: allPromotions.length,
    displayedPromotions: promotions.length
  };
};

export default usePromotions;